import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";
// import { apiClient } from "@/lib/apiClient";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import Breadcrumb from "@/components/ui/breadcrumb";
import Input from "@/components/ui/input";
import Textarea from "@/components/ui/textarea";
import Button from "@/components/ui/button";
import ConfirmModal from "@/components/ui/confirm-modal";
// import Dropdown from "@/components/ui/dropdown";
import DatePicker from "@/components/ui/datepicker";
import {
  LeftChevron,
  ArrowRightLong,
  ArrowLeftLong,
  Pencil,
  Exam,
  Eye,
  Check,
} from "@/components/icons";
import PollQuestionsStep, {
  PollQuestionData,
} from "../components/PollQuestionsStep";
import PollPreviewStep from "../components/PollPreviewStep";
import { pollsApi, mapQuestionTypeToBackend } from "../services/polls.api";
import { SurveyStatus, type CreateSurveyDto } from "../types/polls.types";
import styles from "./CreatePollPage.module.scss";

const STEPS = [
  { key: "basic", labelKey: "createPoll.steps.basicInfo", icon: Pencil },
  {
    key: "questions",
    labelKey: "createPoll.steps.pollQuestions",
    icon: Exam,
  },
  { key: "preview", labelKey: "createPoll.steps.preview", icon: Eye },
] as const;

interface BasicInfoFormData {
  pollName: string;
  pollDescription: string;
  // educationID: string;
  startDate: string;
  endDate: string;
}

/** Zod: Temel bilgiler – CreateSurveyDto kurallarına uygun (name max 255, description max 2000) */
const createPollBasicInfoSchema = z
  .object({
    pollName: z
      .string()
      .min(1, "createPoll.validation.nameRequired")
      .max(255, "createPoll.validation.nameMaxLength"),
    pollDescription: z
      .string()
      .max(2000, "createPoll.validation.descriptionMaxLength")
      .optional(),
    startDate: z.string(),
    endDate: z.string(),
  })
  .refine(
    (data) => {
      if (!data.startDate.trim() || !data.endDate.trim()) return true;
      const start = parseISODate(data.startDate);
      const end = parseISODate(data.endDate);
      if (!start || !end) return true;
      return end >= start;
    },
    {
      message: "createPoll.validation.endBeforeStart",
      path: ["endDate"],
    },
  );

function validateBasicInfoStep(
  formData: BasicInfoFormData,
):
  | { success: true }
  | { success: false; errors: string[]; fieldErrors: Record<string, string> } {
  const result = createPollBasicInfoSchema.safeParse({
    pollName: formData.pollName,
    pollDescription: formData.pollDescription,
    startDate: formData.startDate,
    endDate: formData.endDate,
  });
  if (result.success) return { success: true };
  const errors = result.error.flatten();
  const messages: string[] = [
    ...(errors.formErrors ?? []),
    ...Object.values(errors.fieldErrors)
      .flat()
      .filter((m): m is string => typeof m === "string"),
  ];
  const fieldErrors: Record<string, string> = {};
  Object.entries(errors.fieldErrors ?? {}).forEach(([key, val]) => {
    const first = Array.isArray(val) ? val[0] : val;
    if (typeof first === "string") fieldErrors[key] = first;
  });
  return { success: false, errors: messages, fieldErrors };
}

function validateCreatePollForm(
  formData: BasicInfoFormData,
  questions: PollQuestionData[],
  t: TFunction,
):
  | { success: true }
  | { success: false; errors: string[]; fieldErrors: Record<string, string> } {
  const basicResult = validateBasicInfoStep(formData);
  if (!basicResult.success) return basicResult;
  if (questions.length === 0) {
    return {
      success: false,
      errors: [
        t("createPoll.validation.questionsRequired"),
      ],
      fieldErrors: {},
    };
  }
  return { success: true };
}

/** Seçilen tarihi yerel gün olarak YYYY-MM-DD saklamak için (DatePicker’da doğru gün görünsün) */
function dateToYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Date objesini ISO formatına çevirir (backend: startDate 00:00, endDate 23:59) */
function dateToISOStart(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}T00:00:00.000Z`;
}

function dateToISOEnd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}T23:59:59.000Z`;
}

// interface AdminEducationItem {
//   id: string;
//   name?: string;
// }

function parseISODate(value: string): Date | null {
  if (!value || value.trim() === "") return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function buildCreateSurveyPayload(
  formData: BasicInfoFormData,
  questions: PollQuestionData[],
  options?: { isDraft?: boolean },
): CreateSurveyDto {
  const payload: CreateSurveyDto = {
    name: formData.pollName.trim(),
    description: formData.pollDescription.trim() || "",
    educationID: "", // İlişkili eğitim seçimi yorum satırına alındı
    questions: questions.map((q, index) => ({
      type: mapQuestionTypeToBackend(q.type),
      questionText: q.questionText.trim() || "",
      isRequired: q.required,
      order: index + 1,
      options:
        q.options && q.options.length > 0
          ? q.options
              .filter((opt) => opt.value.trim() !== "")
              .map((opt, optIndex) => ({
                optionText: opt.value.trim(),
                order: optIndex,
              }))
          : undefined,
    })),
  };
  if (formData.startDate.trim()) {
    const d = parseISODate(formData.startDate);
    if (d) payload.startDate = dateToISOStart(d);
  }
  if (formData.endDate.trim()) {
    const d = parseISODate(formData.endDate);
    if (d) payload.endDate = dateToISOEnd(d);
  }
  if (options?.isDraft) {
    payload.status = SurveyStatus.DRAFT; // 0 - Taslak
  }
  return payload;
}

const CreatePollPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<BasicInfoFormData>({
    pollName: "",
    pollDescription: "",
    // educationID: "",
    startDate: "",
    endDate: "",
  });
  // const [educationOptions, setEducationOptions] = useState<
  //   { label: string; value: string }[]
  // >([]);
  const [questions, setQuestions] = useState<PollQuestionData[]>([]);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Eğitim listesi (Temel Bilgiler adımında seçim için) - İlişkili eğitim seçimi yorum satırına alındı
  // useEffect(() => {
  //   apiClient
  //     .get<{ data?: AdminEducationItem[] }>("/api/admin/educations", {
  //       params: { limit: 100, page: 1 },
  //     })
  //     .then(({ data }) => {
  //       const items = Array.isArray(data?.data) ? data.data : [];
  //       setEducationOptions(
  //         items
  //           .filter((e) => e.id)
  //           .map((e) => ({
  //             label: e.name ?? e.id,
  //             value: e.id,
  //           }))
  //       );
  //     })
  //     .catch(() => setEducationOptions([]));
  // }, []);

  const handleGoBack = () => {
    navigate("/dashboard/polls");
  };

  const breadcrumbItems = [
    {
      label: t("createPoll.breadcrumb.polls"),
      onClick: () => navigate("/dashboard/polls"),
    },
    {
      label: t("createPoll.breadcrumb.createPoll"),
    },
  ];

  const handleInputChange = (field: keyof BasicInfoFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      if (currentStep === 0) {
        const validation = validateBasicInfoStep(formData);
        if (!validation.success) {
          setFieldErrors(validation.fieldErrors);
          toast.error(
            validation.errors[0] ??
              t("createPoll.validation.invalid"),
          );
          return;
        }
        setFieldErrors({});
      }
      if (currentStep === 1) {
        if (questions.length === 0) {
          toast.error(
            t("createPoll.validation.questionsRequired"),
          );
          return;
        }
      }
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSaveDraft = async () => {
    const validation = validateCreatePollForm(formData, questions, t);
    if (!validation.success) {
      setFieldErrors(validation.fieldErrors);
      toast.error(
        validation.errors[0] ??
          t("createPoll.validation.invalid"),
      );
      return;
    }
    setFieldErrors({});
    setIsSavingDraft(true);
    try {
      const payload = buildCreateSurveyPayload(formData, questions, {
        isDraft: true,
      });
      await pollsApi.create(payload);
      toast.success(
        t("createPoll.draftSuccess"),
      );
      navigate("/dashboard/polls");
    } catch (error) {
      toast.error(
        t("createPoll.draftError"),
      );
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handlePublish = () => {
    const validation = validateCreatePollForm(formData, questions, t);
    if (!validation.success) {
      setFieldErrors(validation.fieldErrors);
      toast.error(
        validation.errors[0] ??
          t("createPoll.validation.invalid"),
      );
      return;
    }
    setFieldErrors({});
    setIsPublishModalOpen(true);
  };

  const handleConfirmPublish = async () => {
    setIsPublishing(true);
    try {
      const payload = buildCreateSurveyPayload(formData, questions);
      await pollsApi.create(payload);
      setIsPublishModalOpen(false);
      navigate("/dashboard/polls", { state: { pollCreatedSuccess: true } });
    } catch (error) {
      toast.error(
        t("createPoll.publishError"),
      );
    } finally {
      setIsPublishing(false);
    }
  };

  // Calculate estimated duration based on question count (approx 30 seconds per question)
  // const getEstimatedDuration = () => {
  //   const totalSeconds = questions.length * 30;
  //   const minutes = Math.ceil(totalSeconds / 60);
  //   return `${minutes} dk`;
  // };

  const handleQuestionsChange = (updatedQuestions: PollQuestionData[]) => {
    setQuestions(updatedQuestions);
  };

  return (
    <DashboardLayout>
      <Header title={t("polls.title")} />

      <div className={styles.container}>
        <div className={styles.pageHeader}>
          <div className={styles.breadcrumbContainer}>
            <div
              role="button"
              tabIndex={0}
              className={styles.backButton}
              onClick={handleGoBack}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleGoBack();
                }
              }}
              aria-label={t("common.back")}
            >
              <LeftChevron width={20} height={20} />
            </div>
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>

        <div className={styles.stepperContainer}>
          {currentStep > 0 && (
            <Button
              type="button"
              variant="text"
              color="secondary"
              className={styles.inlineBackButton}
              startIcon={<ArrowLeftLong width={20} height={20} />}
              onClick={handlePrev}
            >
              {t("common.back")}
            </Button>
          )}
          <div className={styles.stepperRow}>
            <div className={styles.stepper}>
              {STEPS.map((step, index) => {
                const Icon = step.icon;
                const isActive = index === currentStep;
                const isChecked = index < currentStep;
                const connectorStatus =
                  index < STEPS.length - 1
                    ? currentStep > index
                      ? "completed"
                      : currentStep === index
                        ? "current"
                        : "future"
                    : null;
                return (
                  <React.Fragment key={step.key}>
                    <div
                      role="button"
                      tabIndex={0}
                      className={`${styles.step} ${isActive ? styles.stepActive : ""} ${isChecked ? styles.stepChecked : ""}`}
                      onClick={() => setCurrentStep(index)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setCurrentStep(index);
                        }
                      }}
                    >
                      <span className={styles.stepIcon}>
                        {isChecked ? (
                          <Check width={24} height={24} color="#fff" />
                        ) : (
                          <Icon width={24} height={24} />
                        )}
                      </span>
                      <span className={styles.stepLabel}>
                        {t(
                          step.labelKey,
                          step.key === "basic"
                            ? "Temel Bilgiler"
                            : step.key === "questions"
                              ? t("createPoll.steps.questions")
                              : t("createPoll.steps.preview"),
                        )}
                      </span>
                    </div>
                    {connectorStatus && (
                      <div className={styles.stepConnectorWrapper} aria-hidden>
                        <div
                          className={`${styles.stepConnector} ${connectorStatus === "completed" ? styles.stepConnectorCompleted : connectorStatus === "current" ? styles.stepConnectorCurrent : ""}`}
                        />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
          <div className={styles.nextButtonWrap}>
            {currentStep < STEPS.length - 1 && (
              <Button
                type="button"
                variant="text"
                color="primary"
                endIcon={<ArrowRightLong width={20} height={20} />}
                onClick={handleNext}
              >
                {t("createPoll.next")}
              </Button>
            )}
            {currentStep === STEPS.length - 1 && (
              <div className={styles.actionButtons}>
                <Button
                  variant="soft"
                  color="primary"
                  className={styles.draftButton}
                  onClick={handleSaveDraft}
                  loading={isSavingDraft}
                  disabled={isPublishing}
                >
                  {t("createPoll.actions.draft")}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  className={styles.publishButton}
                  onClick={handlePublish}
                  disabled={isSavingDraft}
                >
                  {t("createPoll.actions.publish")}
                </Button>
              </div>
            )}
          </div>
        </div>

        {currentStep === 0 && (
          <div className={styles.formCard}>
            <h2 className={styles.cardTitle}>
              {t("createPoll.basicInfo.title")}
            </h2>
            <p className={styles.cardDescription}>
              {t("createPoll.basicInfo.description")}
            </p>
            <div className={styles.formGrid}>
              <div className={styles.formField}>
                <Input
                  label={t("createPoll.basicInfo.pollName")}
                  placeholder={t("createPoll.basicInfo.pollNamePlaceholder")}
                  value={formData.pollName}
                  maxLength={255}
                  onChange={(e) =>
                    handleInputChange("pollName", e.target.value)
                  }
                  error={!!fieldErrors.pollName}
                  errorMessage={fieldErrors.pollName}
                />
              </div>
              {/* İlişkili eğitim seçimi yorum satırına alındı
              <div className={styles.formField}>
                <Dropdown
                  label={t("createPoll.basicInfo.relatedEducation")}
                  placeholder={t("createPoll.basicInfo.relatedEducationPlaceholder")}
                  options={educationOptions}
                  value={formData.educationID || null}
                  onSelect={(value) =>
                    handleInputChange("educationID", typeof value === "string" ? value : "")
                  }
                  searchable
                />
              </div>
              */}
              <div className={styles.formField}>
                <Textarea
                  label={t("createPoll.basicInfo.pollDescription")}
                  placeholder={t("createPoll.basicInfo.pollDescriptionPlaceholder")}
                  value={formData.pollDescription}
                  maxLength={2000}
                  onChange={(e) =>
                    handleInputChange("pollDescription", e.target.value)
                  }
                  error={!!fieldErrors.pollDescription}
                  errorMessage={fieldErrors.pollDescription}
                />
              </div>
              <div className={styles.dateRow}>
                <div className={styles.formField}>
                  <DatePicker
                    label={t("createPoll.basicInfo.startDate")}
                    placeholder={t("createPoll.basicInfo.datePlaceholder")}
                    value={parseISODate(formData.startDate)}
                    onChange={(value) => {
                      const date = Array.isArray(value) ? value[0] : value;
                      handleInputChange(
                        "startDate",
                        date ? dateToYmd(date) : "",
                      );
                    }}
                    format="dd.MM.yyyy"
                    locale="tr"
                    variant="outlined"
                  />
                </div>
                <div className={styles.formField}>
                  <DatePicker
                    label={t("createPoll.basicInfo.endDate")}
                    placeholder={t("createPoll.basicInfo.datePlaceholder")}
                    value={parseISODate(formData.endDate)}
                    minDate={parseISODate(formData.startDate) ?? undefined}
                    onChange={(value) => {
                      const date = Array.isArray(value)
                        ? (value[1] ?? value[0])
                        : value;
                      handleInputChange("endDate", date ? dateToYmd(date) : "");
                    }}
                    format="dd.MM.yyyy"
                    locale="tr"
                    variant="outlined"
                    error={!!fieldErrors.endDate}
                    errorMessage={fieldErrors.endDate}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {currentStep === 1 && (
          <PollQuestionsStep
            questions={questions}
            onQuestionsChange={handleQuestionsChange}
          />
        )}

        {currentStep === 2 && (
          <PollPreviewStep formData={formData} questions={questions} />
        )}
      </div>

      <ConfirmModal
        isOpen={isPublishModalOpen}
        onClose={() => setIsPublishModalOpen(false)}
        onConfirm={handleConfirmPublish}
        title={t("createPoll.publishModal.title")}
        message=""
        confirmText={t("createPoll.publishModal.confirm")}
        cancelText={t("createPoll.publishModal.cancel")}
        loading={isPublishing}
        pollInfo={{
          pollName:
            formData.pollName ||
            t("createPoll.publishModal.defaultPollName"),
          questionCount: questions.length,
          description:
            formData.pollDescription ||
            t("createPoll.publishModal.noDescription"),
          startDate: formData.startDate
            ? new Date(formData.startDate).toLocaleDateString("tr-TR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })
            : undefined,
          endDate: formData.endDate
            ? new Date(formData.endDate).toLocaleDateString("tr-TR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })
            : undefined,
        }}
      />
    </DashboardLayout>
  );
};

export default CreatePollPage;
